import { useSortable } from '@dnd-kit/sortable';
import type { MantineColor } from '@mantine/core';
import { Box, createStyles, Group, Stack } from '@mantine/core';
import { Icon, Text, Title } from '@repo/foundations';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import type { IWidget } from '../../../../../../../api';
import { openConfirmModal } from '../../../../../../../components/ModalManager';
import type { WidgetSize } from '../../../../../../../interfaces';
import { WidgetType } from '../../../../../../../interfaces';
import { useHomepageTeam } from '../../../../../hooks/useHomepageTeam';
import type {
	HomepageWidgetType,
	WidgetRemoveFn,
	WidgetUpdateFn,
} from '../../../../../types';
import { WIDGET_HEIGHT, WIDGET_ICON_MAP } from '../../../constants';
import {
	getAnimation,
	getTransition,
	getWidgetColors,
	getWidgetSpan,
	getWidgetTitle,
	isTextWidget,
} from '../../../utils';
import WidgetMenu from './WidgetMenu';

interface IStyleParams {
	height?: number;
	color?: string;
	category?: 'ai' | 'default';
}

type IWidgetWrapperProps = {
	widget: IWidget;
	children: React.ReactNode;
	withMenu?: boolean;
	onUpdateWidget: WidgetUpdateFn;
	onRemoveWidget: WidgetRemoveFn;
	filter?: React.ReactNode;
	right?: React.ReactNode;
	category?: 'ai' | 'default';
};

const useStyles = createStyles(
	(theme, { color, height = WIDGET_HEIGHT, category }: IStyleParams) => {
		const widgetColors = getWidgetColors(color, theme);

		return {
			wrapper: {
				display: 'flex',
				height,
				width: '100%',
				borderRadius: theme.other.borderRadius.sm,
				backgroundColor: theme.other.getColor('surface/primary/default'),
				...widgetColors,
			},
			content: {
				width: '100%',
			},
			header: {
				borderTopLeftRadius: theme.other.borderRadius.sm,
				borderTopRightRadius: theme.other.borderRadius.sm,
				border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
				borderBottom: 0,
			},
			title: {
				cursor: 'grab',
				flexGrow: 1,
				height: '100%',
				display: 'inline-flex',
				alignItems: 'center',
				color:
					category === 'ai'
						? theme.other.getColor('text/ai/default')
						: theme.other.getColor('text/secondary/default'),
			},
			titleIcon: {
				paddingTop: 2,
			},
			grabbing: {
				cursor: 'grabbing',
			},
			innerContent: {
				width: '100%',
				height: '100%',
				paddingTop: 0,
				flexGrow: 1,
				overflowY: 'auto',
				overflowX: 'hidden',
			},
			cursorDefault: {
				cursor: 'default',
			},
		};
	}
);

function WidgetWrapper({
	widget,
	children,
	withMenu = false,
	onUpdateWidget,
	onRemoveWidget,
	filter = null,
	right,
	category = 'default',
}: IWidgetWrapperProps) {
	const { isTeamViewerUser } = useHomepageTeam();

	const widgetHeight =
		widget.type === WidgetType.INTEGRATIONS && widget.size === 'full'
			? 180
			: undefined;

	const { cx, classes, theme } = useStyles({
		color: widget.color,
		height: widgetHeight,
		category,
	});

	const { id, size, type, title, color } = widget;

	const {
		attributes,
		setNodeRef,
		setActivatorNodeRef,
		listeners,
		transform,
		isDragging,
	} = useSortable({
		id,
		transition: null,
		disabled: isTeamViewerUser,
	});

	const style = {
		gridColumn: getWidgetSpan(size),
		boxShadow: isDragging
			? theme.shadows.lg
			: '0px 8px 16px 0px #0000000A , 0px 0px 0px 1px #23222114 inset',
	};

	const handleSizeChange = (widgetSize: WidgetSize) => () => {
		onUpdateWidget(widget.id, 'size')(widgetSize);
	};

	let handleColorChange: ((color: MantineColor) => void) | undefined;

	if (isTextWidget(widget.type)) {
		handleColorChange = (updatedColor: string) => {
			onUpdateWidget(widget.id, 'color')(updatedColor);
		};
	}

	const handleRemoveWidget = useMemo(
		() => () =>
			openConfirmModal({
				title: <Title order={4}>Remove this widget?</Title>,
				children: <Text>This can’t be undone.</Text>,
				labels: {
					cancel: 'Cancel',
					confirm: 'Remove widget',
				},
				confirmProps: { variant: 'primary', tone: 'critical' },
				onConfirm: onRemoveWidget(widget.id),
				tone: 'critical',
			}),
		[onRemoveWidget, widget.id]
	);

	const widgetTitle = getWidgetTitle(type as HomepageWidgetType, title);

	const memoizedHeader = useMemo(
		() => (
			<Group
				position="apart"
				align="center"
				noWrap
				py="sm"
				pl="lg"
				pr="sm"
				spacing={0}
				className={classes.header}
				bg={theme.other.getColor('surface/primary/default')}
			>
				<Group spacing="sm" noWrap style={{ minWidth: 0, flex: '0 1 auto' }}>
					<Icon
						name={WIDGET_ICON_MAP[type] || 'folders'}
						size="md"
						color={
							category === 'ai' ? 'icon/ai/default' : 'icon/secondary/default'
						}
					/>
					<Text
						size="sm"
						fw="semibold"
						p="sm"
						lineClamp={1}
						className={cx(classes.title, {
							[classes.grabbing]: isDragging,
							[classes.cursorDefault]: isTeamViewerUser,
						})}
						ref={setActivatorNodeRef}
						{...listeners}
						style={{ minWidth: 0 }}
					>
						<Group align="inherit" spacing={0} noWrap>
							{widgetTitle}
						</Group>
					</Text>
				</Group>
				<Group spacing="xs" noWrap style={{ flex: '0 0 auto' }}>
					{filter}
					{right}
					{withMenu && (
						<WidgetMenu
							widgetType={widget.type}
							size={size}
							color={color}
							onSizeChange={handleSizeChange}
							onRemoveWidget={handleRemoveWidget}
							onColorChange={handleColorChange}
						/>
					)}
				</Group>
			</Group>
		),
		[
			classes.header,
			classes.title,
			classes.cursorDefault,
			classes.grabbing,
			cx,
			isDragging,
			isTeamViewerUser,
			listeners,
			setActivatorNodeRef,
			widgetTitle,
			filter,
			withMenu,
			widget.type,
			size,
			color,
			handleSizeChange,
			handleRemoveWidget,
			handleColorChange,
			right,
			category,
			type,
			theme.other,
		]
	);

	const memoizedContent = useMemo(
		() => <Box className={classes.innerContent}>{children}</Box>,
		[classes.innerContent, children]
	);

	return (
		<motion.div
			className={classes.wrapper}
			ref={setNodeRef}
			layoutId={String(id)}
			style={style}
			animate={{
				...getAnimation(transform, isDragging),
			}}
			transition={getTransition(isDragging)}
			data-testid={`${type}-widget-wrapper`}
			{...attributes}
		>
			<Stack className={classes.content} spacing={0}>
				{memoizedHeader}
				{memoizedContent}
			</Stack>
		</motion.div>
	);
}

export default WidgetWrapper;
